import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { getSettings } from './utils/settings';
import { ValidationResults } from './types';
import { useProjectConfig } from './hooks/useProjectConfig';
import EnhancedValidationResultsDisplay from './components/EnhancedValidationResultsDisplay';
import LoadingSkeleton from './components/LoadingSkeleton';
import PVQSampleLoader from './components/PVQSampleLoader';
import SidebarNavigation from './components/SidebarNavigation';
import PassportForm from './components/PassportForm';
import ComprehensiveReview from './components/ComprehensiveReview';
import FormerFederalService from './components/FormerFederalService';
import FinancialJudgment from './components/FinancialJudgment';
import ForeignContact from './components/ForeignContact';
import Settings from './components/Settings';
import PVQPOCSettings from './components/PVQPOCSettings';
import NOCDashboard from './components/NOCDashboard';
import ClaudeArtifactViewer from './components/ClaudeArtifactViewer';
import ExecutiveDashboard from './components/analytics/ExecutiveDashboard';
import PDTUpload from './components/PDTUpload';
import SystemArchitecture from './components/SystemArchitecture';
import PIAlignmentReadOnly from './components/PIAlignmentReadOnly';
import PIAlignmentEditor from './components/PIAlignmentEditor';
import RAGTesting from './components/RAGTesting';
// RAG Testing components
import ForeignContactSEAD3Test from './components/rag-test/ForeignContactSEAD3Test';
import ForeignContactComparison from './components/rag-test/ForeignContactComparison';
import ForeignTravelTest from './components/rag-test/ForeignTravelTest';
import FinancialMattersTest from './components/rag-test/FinancialMattersTest';
import SubstanceTreatmentTest from './components/rag-test/SubstanceTreatmentTest';
import PersonalRelationshipsTest from './components/rag-test/PersonalRelationshipsTest';
import LegalMattersTest from './components/rag-test/LegalMattersTest';
import ForeignActivitiesTest from './components/rag-test/ForeignActivitiesTest';
import MediaContactTest from './components/rag-test/MediaContactTest';
import ReportableActionsByOthersTest from './components/rag-test/ReportableActionsByOthersTest';
import FieldContextTuning from './components/rag-test/FieldContextTuning';

// Sample PVQ data for testing
const samplePVQData = {
  "section_9_citizenship": {
    "birth_location": "Germany",
    "citizenship_selection": "US citizen by birth in US territory"
  },
  "section_11_residence": [
    {
      "address": "123 Main St, College Park, MD",
      "start_date": "2023-07-01",
      "end_date": "2025-07-01"
    }
  ],
  "section_12_education": [
    {
      "school": "University of Maryland",
      "start_date": "2021-08-01",
      "end_date": "2025-05-01",
      "verifier": {
        "name": "John Smith Sr",
        "relationship": "Father",
        "phone": "I don't know",
        "address": "Unknown"
      }
    }
  ],
  "section_13a_employment": [
    {
      "employer": "Ford Motor Company",
      "start_date": "2024-05-01",
      "end_date": "2024-09-01",
      "position": "Engineer"
    },
    {
      "employer": "Unemployment",
      "start_date": "2023-08-01",
      "end_date": "2024-09-01",
      "position": "Unemployed"
    }
  ],
  "section_15_military": [
    {
      "service_start": "2014-08-01",
      "service_end": "2018-08-01",
      "discharge_date": "2018-10-01"
    }
  ],
  "section_18_relatives": [
    {
      "name": "Jane Smith",
      "relationship": "Mother"
    }
  ]
};

// AppContent component that uses routing
function AppContent() {
  const projectConfig = useProjectConfig();
  const [results, setResults] = useState<ValidationResults | null>(null);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pvqData, setPvqData] = useState(JSON.stringify(samplePVQData, null, 2));

  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is on review page and it's disabled, redirect them
  useEffect(() => {
    const settings = getSettings();
    if (location.pathname === '/review' && !settings.performComprehensiveReview) {
      navigate('/passport');
    }
  }, [location.pathname, navigate]);

  // Determine active section from URL
  const getActiveSectionFromPath = (pathname: string) => {
    const path = pathname.slice(1); // Remove leading slash
    if (!path) return 'passport'; // Default to passport for root
    return path;
  };

  const activeSection = getActiveSectionFromPath(location.pathname);

  const validatePVQ = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setRawResponse(null);

    try {
      const parsedData = JSON.parse(pvqData);
      
      // Call API Gateway endpoint
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'https://625x3rqvui.execute-api.us-east-1.amazonaws.com/prod/validate';
      
      console.log('Calling API Gateway:', apiEndpoint);
      console.log('Payload size:', JSON.stringify({ pvq_data: parsedData }).length, 'bytes');
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pvq_data: parsedData })
      });
      
      console.log('API Gateway response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('API Gateway response:', responseData);
      
      // Store raw response for debugging/demo purposes
      setRawResponse(responseData);
      
      if (responseData.success) {
        setResults(responseData.validation_results);
      } else {
        setError(responseData.error || 'Validation failed');
      }
    } catch (err: any) {
      console.error('Validation error:', err);
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error: Unable to connect to validation service. Please check your internet connection.');
      } else if (err.message?.includes('JSON')) {
        setError('Invalid JSON format in PVQ data.');
      } else if (err.message?.includes('CORS')) {
        setError('CORS error: API Gateway configuration issue.');
      } else {
        setError(`Validation error: ${err.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };


  const renderValidatorContent = () => (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              PVQ Data Input
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="pvq-data" className="block text-sm font-medium text-gray-700 mb-2">
                  PVQ JSON Data
                </label>
                <textarea
                  id="pvq-data"
                  value={pvqData}
                  onChange={(e) => setPvqData(e.target.value)}
                  className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue font-mono text-sm"
                  placeholder="Enter PVQ data in JSON format..."
                />
              </div>
              
              <button
                onClick={validatePVQ}
                disabled={loading}
                className="w-full bg-gov-blue text-white py-3 px-6 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-gov-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Validating...
                  </span>
                ) : (
                  'Validate PVQ'
                )}
              </button>
            </div>
          </div>

          {/* Sample File Loader */}
          <PVQSampleLoader onSampleSelected={setPvqData} />

          {/* Instructions */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-green-900 mb-3">
              Instructions
            </h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li>• Choose a sample file above OR paste your PVQ data in JSON format</li>
              <li>• The validator calls AWS Lambda to check for common FSO rejection patterns</li>
              <li>• Results include critical issues, warnings, and recommendations</li>
              <li>• Use sample files to explore different validation scenarios</li>
            </ul>
          </div>

          {/* API Gateway Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <h4 className="text-blue-800 font-medium text-sm">API Gateway Integration</h4>
                <p className="text-blue-700 text-xs mt-1">
                  This app calls your AWS Lambda function via API Gateway. No AWS credentials required in browser.
                  The validation service is securely hosted on AWS infrastructure.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {loading && <LoadingSkeleton />}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-red-900 mb-2">
                Validation Error
              </h3>
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {results && <EnhancedValidationResultsDisplay results={results} rawResponse={rawResponse} />}
          
          {!loading && !error && !results && (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <div className="text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium">No validation results yet</p>
                <p className="text-sm mt-1">Submit PVQ data to see validation results</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gov-blue text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{projectConfig.name}</h1>
              <p className="text-blue-100 mt-1">{projectConfig.description}</p>
            </div>
            <div className="text-right text-sm text-blue-100">
              <p>Defense Counterintelligence and Security Agency</p>
              <p>National Background Investigation Services</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex">
        {/* Sidebar Navigation */}
        <SidebarNavigation activeSection={activeSection} />

        {/* Content */}
        <Routes>
          <Route path="/" element={<PassportForm />} />
          <Route path="/passport" element={<PassportForm />} />
          <Route path="/federal-service" element={<FormerFederalService />} />
          <Route path="/financial-judgment" element={<FinancialJudgment />} />
          <Route path="/foreign-contact" element={<ForeignContact />} />
          <Route path="/review" element={<ComprehensiveReview />} />
          <Route path="/poc-settings" element={<PVQPOCSettings />} />
          <Route path="/validator" element={renderValidatorContent()} />
          <Route path="/artifact-viewer" element={<ClaudeArtifactViewer />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/noc" element={<NOCDashboard />} />
          <Route path="/analytics" element={<ExecutiveDashboard />} />
          <Route path="/pdt-upload" element={<PDTUpload />} />
          <Route path="/architecture" element={<SystemArchitecture />} />
          <Route path="/roadmap" element={<PIAlignmentReadOnly />} />
          <Route path="/roadmap/edit" element={<PIAlignmentEditor />} />
          <Route path="/rag-testing" element={<RAGTesting />} />
          {/* RAG Testing Pages */}
          <Route path="/rag-test/foreign-contact-reporting" element={<ForeignContactSEAD3Test />} />
          <Route path="/rag-test/foreign-contact-reporting-sead3" element={<ForeignContactSEAD3Test />} />
          <Route path="/rag-test/comparison" element={<ForeignContactComparison />} />
          <Route path="/rag-test/foreign-travel-reporting" element={<ForeignTravelTest />} />
          <Route path="/rag-test/financial-matters-reporting" element={<FinancialMattersTest />} />
          <Route path="/rag-test/substance-treatment-reporting" element={<SubstanceTreatmentTest />} />
          <Route path="/rag-test/personal-relationships-reporting" element={<PersonalRelationshipsTest />} />
          <Route path="/rag-test/legal-matters-reporting" element={<LegalMattersTest />} />
          <Route path="/rag-test/foreign-activities-reporting" element={<ForeignActivitiesTest />} />
          <Route path="/rag-test/media-contact-reporting" element={<MediaContactTest />} />
          <Route path="/rag-test/reportable-actions-by-others" element={<ReportableActionsByOthersTest />} />
          <Route path="/rag-test/field-context-tuning" element={<FieldContextTuning />} />
        </Routes>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">About This Tool</h4>
              <p className="text-gray-300 text-sm">
                The PVQ System helps manage Personnel Vetting Questionnaire submissions 
                and provides validation tools for DCSA processing.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <p className="text-gray-300 text-sm">
                For technical support or questions about this tool, 
                contact your local FSO or security office.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <p className="text-gray-300 text-sm">
                Additional resources and documentation available through your security office.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-6 text-center text-gray-400 text-sm">
            <p>© 2024 Defense Counterintelligence and Security Agency. For official use only.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Main App component with Router wrapper
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;